import { SEARCH_FILTERS } from '~~/shared/search-constants'
import { resolveHomeSearchRoute } from '~/utils/home-search-route'

/**
 * Logique de la barre de recherche du hero de la page d'accueil
 * (`HeroSearchPill.vue` consommé par `HeroHomeV3.vue`).
 *
 * Fournit :
 * - État réactif : searchQuery, advancedOpen, localDateRange, localAdults,
 *   localChildren, localChildrenAges, dateHintVisible, persistedCriteriaCount
 * - Handlers : handleSearch, handleSuggestionSelect, handleAdvancedSearch,
 *   saveAdvancedCriteria, clearAdvancedCriteria, incr/decr adults/children,
 *   updateChildAge
 * - Computed : advancedSummary, childAgeOptions, todayISO
 */
export function useHomeSearch() {
  const { t, locale } = useI18n()
  const router = useRouter()
  const localePath = useLocalePath()
  const { capture } = usePostHog()

  const searchQuery = ref('')

  // Type d'hébergement sélectionné sur la home (HomeTypeSwitcher via useHomeType).
  // Source de vérité = query param `?type=cdh|gite` (absent = all). On le lit ici
  // pour propager le type dans la navigation vers /search (spec 4.28.0) — sinon
  // le switcher home ne modifiait que le rendu home, jamais les résultats.
  const { type: homeType } = useHomeType()

  // Suggestion structurée (département/région/ville/site touristique) en attente de validation par "Rechercher"
  const _pendingLocation = ref<{ type: string; value: string; code?: string; slug?: string; lat?: number; lng?: number } | null>(null)

  // Indicateur visuel : la loupe pulse dès qu'il y a quelque chose à valider
  // (texte saisi ou suggestion sélectionnée non encore appliquée). Calculé en computed
  // pour réagir automatiquement à la saisie utilisateur.
  const searchPulse = computed(() =>
    !!_pendingLocation.value || searchQuery.value.trim().length > 0,
  )

  // Persistance des critères avancés (dates + voyageurs) en sessionStorage
  const { load: loadCriteria, save: saveCriteria, clear: clearCriteria, count: criteriaCount } = useSearchCriteria()

  // Nombre de critères persistés pour le badge (hydratation client uniquement)
  const persistedCriteriaCount = ref(0)

  if (import.meta.client) {
    persistedCriteriaCount.value = criteriaCount()
  }

  // État du popover recherche avancée
  const advancedOpen = ref(false)

  const todayISO = new Date().toISOString().split('T')[0]!

  const localDateRange = ref<{ start: string; end: string } | undefined>(undefined)

  // Compteurs voyageurs (spec 4.107.0, révisée en 4.108.0) : règle « tous les
  // âges ou aucun » mutualisée ; le rendu du compteur vit dans HoliGuestCounter.
  const {
    adults: localAdults,
    children: localChildren,
    childrenAges: localChildrenAges,
    showChildrenAges,
    outgoingChildrenAges,
    setAdults,
    setChildren,
    updateChildAge,
    resetGuests,
  } = useGuestCounters()

  // Options d'âge pour le sélecteur enfant (0-17)
  const childAgeOptions = computed((): { label: string; value: number }[] => {
    const options: { label: string; value: number }[] = []
    for (let i = SEARCH_FILTERS.MIN_CHILD_AGE; i <= SEARCH_FILTERS.MAX_CHILD_AGE; i++) {
      options.push({ label: t('common.search.childAgeOption', i), value: i })
    }
    return options
  })

  // Charger les critères persistés au montage (client uniquement)
  if (import.meta.client) {
    const saved = loadCriteria()
    if (saved) {
      if (saved.checkIn && saved.checkOut) {
        localDateRange.value = { start: saved.checkIn, end: saved.checkOut }
      }
      resetGuests(
        saved.minAdults ?? localAdults.value,
        saved.minChildren ?? localChildren.value,
        saved.childrenAges ? [...saved.childrenAges] : [],
      )
    }
  }

  function saveAdvancedCriteria() {
    const criteria: Record<string, unknown> = {}
    if (localDateRange.value) {
      criteria.checkIn = localDateRange.value.start
      criteria.checkOut = localDateRange.value.end
    }
    if (localAdults.value > 0) criteria.minAdults = localAdults.value
    if (localChildren.value > 0) {
      criteria.minChildren = localChildren.value
      // Grand groupe : aucun âge saisi → on ne persiste pas de tableau incohérent.
      if (outgoingChildrenAges.value.length > 0) {
        criteria.childrenAges = outgoingChildrenAges.value
      }
    }
    saveCriteria(criteria)
    persistedCriteriaCount.value = criteriaCount()
  }

  // Date hint : ring + message sur le date picker quand voyageurs modifiés mais pas de dates
  const dateHintVisible = ref(false)
  let _dateHintTimer: ReturnType<typeof setTimeout> | undefined
  let _dateHintShownOnce = false

  function needsDateHint(): boolean {
    const hasCustomGuests = localAdults.value !== SEARCH_FILTERS.DEFAULT_ADULTS || localChildren.value > 0
    return hasCustomGuests && !localDateRange.value
  }

  function showDateHintIfNeeded() {
    if (needsDateHint()) {
      dateHintVisible.value = true
      if (_dateHintTimer) clearTimeout(_dateHintTimer)
      _dateHintTimer = setTimeout(() => { dateHintVisible.value = false }, 5000)
    }
  }

  function clearAdvancedCriteria() {
    localDateRange.value = undefined
    resetGuests(SEARCH_FILTERS.DEFAULT_ADULTS, SEARCH_FILTERS.DEFAULT_CHILDREN, [])
    dateHintVisible.value = false
    clearCriteria()
    persistedCriteriaCount.value = 0
    advancedOpen.value = false
  }

  // Masquer le hint dès que des dates sont sélectionnées
  watch(localDateRange, (val) => {
    if (val) {
      dateHintVisible.value = false
      if (_dateHintTimer) clearTimeout(_dateHintTimer)
    }
  })

  // Sauvegarder les critères avancés quand le popover se ferme + reset hint à l'ouverture
  watch(advancedOpen, (isOpen) => {
    if (isOpen) {
      _dateHintShownOnce = false
    }
    else {
      saveAdvancedCriteria()
    }
  })

  // Résumé des critères avancés pour le label du bouton
  const advancedSummary = computed(() => {
    const parts: string[] = []
    if (localDateRange.value) {
      const fmt = new Intl.DateTimeFormat(locale.value === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short' })
      const [sy, sm, sd] = localDateRange.value.start.split('-').map(Number)
      const [ey, em, ed] = localDateRange.value.end.split('-').map(Number)
      parts.push(`${fmt.format(new Date(sy!, sm! - 1, sd!))} — ${fmt.format(new Date(ey!, em! - 1, ed!))}`)
    }
    if (localAdults.value > 0 || localChildren.value > 0) {
      const gParts: string[] = []
      if (localAdults.value > 0) gParts.push(t('common.search.adultsCount', { count: localAdults.value }))
      if (localChildren.value > 0) gParts.push(t('common.search.childrenCount', { count: localChildren.value }))
      parts.push(gParts.join(', '))
    }
    return parts.join(' · ')
  })

  // Suggestion selected → stockée localement, pas de navigation avant click "Rechercher"
  function handleSuggestionSelect(suggestion: { type: string; value: string; code?: string; slug?: string; lat?: number; lng?: number }) {
    _pendingLocation.value = suggestion
    // searchPulse est computed : il s'active automatiquement via _pendingLocation
  }

  /**
   * Navigation vers la recherche avec query + critères persistés.
   * Département/région avec slug → page PSEO dédiée.
   */
  function handleSearch() {
    // Event produit : recherche lancée (un seul point de capture — handleAdvancedSearch
    // délègue à handleSearch). Aucune PII : on ne capture que destination + critères.
    capture('search_performed', {
      location_type: _pendingLocation.value?.type ?? (searchQuery.value.trim() ? 'freetext' : 'none'),
      has_dates: !!localDateRange.value,
      nights: localDateRange.value
        ? Math.round((new Date(localDateRange.value.end).getTime() - new Date(localDateRange.value.start).getTime()) / 86_400_000)
        : 0,
      adults: localAdults.value,
      children: localChildren.value,
    })

    const loc = _pendingLocation.value
    _pendingLocation.value = null

    // Aiguillage partagé avec le parcours de l'accueil (fonction pure) :
    // département/région → page pSEO typée, sinon `/search`.
    const target = resolveHomeSearchRoute(loc, searchQuery.value, homeType.value, {
      dates: localDateRange.value,
      adults: localAdults.value,
      children: localChildren.value,
      childrenAges: outgoingChildrenAges.value,
    })
    // Seule la recherche `/search` persiste le lieu (comportement historique).
    if (target.locationQuery) saveCriteria({ ...target.locationQuery })
    router.push({
      path: localePath(target.path),
      query: Object.keys(target.query).length > 0 ? target.query : undefined,
    })
  }

  /**
   * Recherche avancée : applique les critères locaux et lance la recherche.
   * 1er clic sans dates et hint jamais montré : montrer le hint et rester ouvert.
   * 2e clic ou dates ok : lancer la recherche.
   */
  function handleAdvancedSearch() {
    saveAdvancedCriteria()

    if (needsDateHint() && !_dateHintShownOnce) {
      showDateHintIfNeeded()
      _dateHintShownOnce = true
      return
    }

    advancedOpen.value = false
    handleSearch()
  }

  return {
    searchQuery,
    advancedOpen,
    todayISO,
    localDateRange,
    localAdults,
    localChildren,
    localChildrenAges,
    showChildrenAges,
    setAdults,
    setChildren,
    dateHintVisible,
    persistedCriteriaCount,
    childAgeOptions,
    advancedSummary,
    searchPulse,
    updateChildAge,
    saveAdvancedCriteria,
    clearAdvancedCriteria,
    handleSuggestionSelect,
    handleSearch,
    handleAdvancedSearch,
  }
}
